import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import {
    forwardRef,
    ForwardedRef,
    ReactElement,
    useCallback,
    useImperativeHandle,
    useRef,
} from 'react';
import { ColumnState, FirstDataRenderedEvent, GridReadyEvent } from 'ag-grid-community';
import {
    createSaveColumnState,
    getRowStyle,
    onGridReady,
    restoreGridScrollState,
    saveGridScrollState,
    withScrollRestoreLoading,
} from 'src/utils/grid/gridStyleUtils';
import { HandledProps, pdaGridConfig } from 'src/components/common/common';
import { useGridCellSelection } from 'src/utils/grid/GridHandlerContent';
import { ShiftRangeSelectionConfig, useShiftRangeRowSelection } from 'src/utils/grid/useShiftRangeRowSelection';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';

interface GridSelectProps<TData> extends Omit<AgGridReactProps<TData>, HandledProps> {
    setHoveredRowIndex: (index: number | null) => void;
    columnStateKey: string;
    setColumnState: (state: ColumnState[]) => void;
    onGridReady?: (params: GridReadyEvent<TData>) => void;
    onFirstDataRendered?: (params: FirstDataRenderedEvent<TData>) => void;
    onColumnMoved?: () => void;
    onColumnPinned?: () => void;
    onColumnVisible?: () => void;
    onColumnResized?: () => void;
    shiftRangeSelection?: ShiftRangeSelectionConfig<TData>;
    scrollTop?: number | null;
    setScrollTop?: (top: number | null) => void;
    restoreScroll?: boolean;
}

function GridSelect<TData extends {} = any>(
    props: GridSelectProps<TData>,
    ref: ForwardedRef<AgGridReact<TData>>
): ReactElement {
    const {
        setHoveredRowIndex,
        columnStateKey,
        setColumnState,
        onGridReady: onGridReadyProp,
        onFirstDataRendered,
        onColumnMoved: onColumnMovedProp,
        onColumnPinned: onColumnPinnedProp,
        onColumnVisible: onColumnVisibleProp,
        onColumnResized: onColumnResizedProp,
        onRowClicked: onRowClickedProp,
        shiftRangeSelection,
        scrollTop,
        setScrollTop,
        restoreScroll = true,
        rowHeight = pdaGridConfig.rowHeight,
        headerHeight = pdaGridConfig.headerHeight,
        ...restProps
    } = props;

    const gridRef = useRef<AgGridReact<TData>>(null);

    const {
        handleCellClick_targeting,
        handleCellCopy,
        handleCellMouseDown,
        handleCellMouseOver,
        clearRange,
        getSelectedCellRange,
        selectedCellRef,
        processedColDefs,
    } = useGridCellSelection({
        columnDefs: props.columnDefs,
    });

    useImperativeHandle(
        ref,
        () => {
            const instance = gridRef.current;
            if (!instance) return null as unknown as AgGridReact<TData>;

            return Object.assign(instance, {
                clearRange: () => {
                    const api = gridRef.current?.api;
                    if (api) clearRange(api);
                },
                getSelectedCellRange,
            }) as AgGridReact<TData>;
        },
        [clearRange, getSelectedCellRange]
    );

    const saveColumnState = createSaveColumnState(gridRef, columnStateKey, setColumnState);

    const handleColumnMoved = () => {
        saveColumnState();
        onColumnMovedProp?.();
    };
    const handleColumnPinned = () => {
        saveColumnState();
        onColumnPinnedProp?.();
    };
    const handleColumnVisible = () => {
        saveColumnState();
        onColumnVisibleProp?.();
    };
    const handleColumnResized = () => {
        saveColumnState();
        onColumnResizedProp?.();
    };

    const onRowClicked = useShiftRangeRowSelection<TData>({
        config: shiftRangeSelection,
        onRowClicked: onRowClickedProp,
    });

    const handleBodyScrollEnd = useCallback(() => {
        const api = gridRef.current?.api;
        if (api && setScrollTop) {
            saveGridScrollState(api, setScrollTop);
        }
    }, [setScrollTop]);

    const { setLoadingSpinner } = useLoadingSpinnerStore();

    const handleFirstDataRendered = useCallback((params: FirstDataRenderedEvent<TData>) => {
        if (restoreScroll && setScrollTop) {
            const shouldRestoreScroll = scrollTop != null && scrollTop >= 0;
            if (shouldRestoreScroll) {
                withScrollRestoreLoading(setLoadingSpinner, (onComplete) => {
                    restoreGridScrollState(params.api, scrollTop, onComplete);
                });
            } else {
                restoreGridScrollState(params.api, scrollTop);
            }
        }
        onFirstDataRendered?.(params);
    }, [restoreScroll, setScrollTop, scrollTop, onFirstDataRendered, setLoadingSpinner]);

    const handleGridReady = (params: GridReadyEvent<TData>) => {
        params.api.setGridOption('context', {
            ...restProps.context,
            cellSelectionRef: selectedCellRef,
        });
        onGridReady(gridRef, columnStateKey, setColumnState);
        onGridReadyProp?.(params);
    };

    return (
        <div className="flex-1 h-full min-h-0 ag-theme-alpine w-full overflow-y-auto relative">
            <AgGridReact<TData>
                ref={gridRef}
                overlayNoRowsTemplate={'<span style="padding: 32px;">조회된 데이터가 없습니다.</span>'}
                {...restProps}
                rowHeight={rowHeight}
                headerHeight={headerHeight}
                popupParent={restProps.popupParent ?? (typeof document !== 'undefined' ? document.body : undefined)}
                enableRangeSelection={true}
                columnDefs={processedColDefs}
                onCellClicked={handleCellClick_targeting}
                onCellMouseDown={handleCellMouseDown}
                onCellMouseOver={(params) => {
                    if (params.node.rowPinned !== 'bottom') {
                        setHoveredRowIndex(params.node.rowIndex);
                    }
                    handleCellMouseOver(params);
                }}
                onCellKeyDown={handleCellCopy}
                onRowClicked={onRowClicked}
                suppressScrollOnNewData={true}
                suppressColumnVirtualisation={true}
                suppressColumnMoveAnimation={true}
                getRowStyle={getRowStyle}
                onCellMouseOut={() => setHoveredRowIndex(null)}
                onGridReady={handleGridReady}
                onFirstDataRendered={handleFirstDataRendered}
                onBodyScrollEnd={handleBodyScrollEnd}
                onColumnMoved={handleColumnMoved}
                onColumnPinned={handleColumnPinned}
                onColumnVisible={handleColumnVisible}
                onColumnResized={handleColumnResized}
                onSortChanged={saveColumnState}
            />
        </div>
    );
}

const Grid_Select = forwardRef(GridSelect) as <TData extends {} = any>(
    props: GridSelectProps<TData> & { ref?: ForwardedRef<AgGridReact<TData>> }
) => ReactElement;

(Grid_Select as React.FunctionComponent).displayName = 'Grid_Select';

export default Grid_Select;
